import {classNames, React, Sentry, useEffect, useLocalStore, observer, toJS, pluralize, useMemo, useAsObservableSource} from '@api'
import {LoadingIndicator, NoProductsFound, ProductLandingComponent, useAssortmentCardStore, useVipLocalStore} from '@design-board/components'
import {useMeasure} from 'react-use'
import {GroupByCatalogRoot_ChildrenGroupsInfinite, RootGroupsStore, GroupByViewRootStoreContext, useGroupByRootStore} from './'
import {Severity} from '@sentry/types'
import * as cssGroupBy from './GroupByCatalogGroups.css'
import * as styles from './RootGroups.css'

export const GROUPS_SCROLL_ID = 'groups-scroller'

/**
 * ElasticSearch Based Grouping Catalog
 *
 * <Root>
 *   <ChildGroups>
 *     <GroupRow>
 *       <Group level={0}>
 *
 *   </ChildGroups>
 * </Root>
 *
 */
export const RootGroups = observer<{ className?: string }>(function RootGroups(props) {
  const assortmentCard = useAssortmentCardStore(),
    obsProps = useAsObservableSource({assortmentCard}),
    store = useVipLocalStore((ws) => new RootGroupsStore(ws, obsProps)),
    {
      strings, catalog, mgr: {registerEffects, rawData, grouping, groupCount, itemCount, totalRowCount, initialLoad, loading, partitionedGroups}, groupName, inlineStyles,
    catalog: {isShowingClientPDF, pdfCatalogStore: {isFrozen}, settings: {imageRatio}, showGroupHeader, itemCount: count, groupBy, groupBy: {hierarchy, settings: {columnView, showExpandedFirst, fixedWidth}}}} = store,

    skip = (isShowingClientPDF && isFrozen && rawData.length > 0),
    invalidGroup = !grouping || (!grouping?.fieldDefinition && !grouping.isOfflineGroup),
    showLoader = !catalog.hasFinishedFirstLoad || initialLoad || groupBy.loading || catalog.itemCount === null || (_.isEmpty(partitionedGroups) && loading)

  // console.log(catalog.hasFinishedFirstLoad, showLoader, loading)
  // console.log({showLoader, groupBy_loading: groupBy.loading, loading, initialLoad})
  // useEffect(() => {
  //   if (showLoader != catalog.loadingState.loading) {
  //     catalog.updateLoadingState({loading: showLoader, loadingMessage: strings.loadingGroups})
  //   }
  // }, [showLoader, catalog.loadingState.loading])

  useEffect(() => registerEffects(), [])

  useEffect(() => {
    if (!loading && !initialLoad && !store.sentry.markedInitialLoad) {
      Sentry.captureEvent({message: `Loaded group by catalog for '${groupName}'`, fingerprint: ['catalog', 'load'], level: Severity.Debug, extra: {groupName, hierarchy}})
      store.sentry.markedInitialLoad = true
    }

    if (!loading && groupBy.loading) {
      groupBy.loading = false
    }
  }, [loading, initialLoad, store.sentry.markedInitialLoad])

  useEffect(() => {
    invalidGroup && !groupName.startsWith('VLP_') && groupBy.reset()
  }, [invalidGroup])

  useEffect(() => {
    return () => {
      catalog.loadingState.loadingMessage = `${strings.loading} ${pluralize(catalog.catalogItemName)}`
    };
  }, []);

  const [ref, {width, height}] = useMeasure()

  // console.log({groupCount, itemCount, loading, initialLoad, rawData: toJS(rawData), queryVariables: toJS(store.mgr.queryVariables), partitionedGroups: toJS(partitionedGroups), order: toJS(grouping.orderBy)})
  return (
    <GroupByViewRootStoreContext.Provider value={store}>
	   <div
       ref={ref}
		   className={classNames(styles.root, props?.className)}
       data-type='RootGroups'
		   data-group={groupName}
		   data-show-expanded-first={showExpandedFirst}
		   data-image-ratio={imageRatio}
		   data-orientation={grouping?.orientation}
       data-column-view={columnView}
       data-products-toolbars={false}
       data-hide-items-on-swatch-hover={groupBy.settings.hideCatalogItemsOnSwatchHover === true}
		   style={{...inlineStyles,
         '--catalog-height': `${height}px`,
         '--catalog-width': `${width}px`} as any}
		   data-group-depth={groupBy.hierarchy.length}
	   >
         {showLoader ? <LoadingIndicator text={grouping ? `Loading ${grouping?.groupTitle.endsWith('?') ? grouping?.groupTitle : pluralize(grouping?.groupTitle)}...` : null}/>
                     : invalidGroup ? <LoadingIndicator text='Switching boards...'/> :
          <div
            className={classNames(cssGroupBy.root)}
            data-view={groupBy.hierarchyGroups[0]?.allowCardMode ? groupBy.settings.groupView : 'flat'}
            data-level={store.level}
            data-fixed-width={fixedWidth}
            data-virtual-rows={totalRowCount || ''}
            data-count={itemCount || ''}
          >
            <div className={styles.groupsContainer} data-type='GroupsContainer'>
              {!initialLoad && !groupBy.loading &&
			   <div className={classNames(styles.groups)}
	          data-type='Groups'
              data-groups={groupCount}
              data-items={itemCount}
              data-level={store.level}
              data-testid="groupByCatalog-rootGroups">
                 {partitionedGroups?.length > 0 ? <GroupByCatalogRoot_ChildrenGroupsInfinite/> : <EmptyView/>}
			   </div>}
            </div>
          </div>
         }
	   </div>

    </GroupByViewRootStoreContext.Provider>
  )
})

RootGroups.displayName='RootGroups'
RootGroups['whyDidYouRender'] = true

const EmptyView = observer(function EmptyView() {
  const {mgr: {online_unassigned, loading},
    catalog, catalog: {itemCount, loadingCounts, settings: {activeSearchText}, groupBy: {settings: {showOnlyExpanded}}}, filter: {hasFilterTerms}} = useGroupByRootStore()

  return (loadingCounts || online_unassigned == 0) && loading
         ? null
         : (hasFilterTerms && itemCount == 0) || activeSearchText || showOnlyExpanded
           ? <NoProductsFound/>
           : <ProductLandingComponent catalog={catalog} searchText={activeSearchText}/>
})



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/RootGroups.tsx